// Room allocation logic translated from VBA macros
import { loadRoomSettings, convertToAllocationFormat } from "./roomSettings";

// Dynamic room settings - loaded from settings or defaults
let currentRoomSettings = null;
let AVAILABLE_ROOMS = [];
let ROOM_CAPACITY = {};

// SESSION-BASED CONFLICT TRACKING SYSTEM
// This preserves conflict numbers throughout the session and tracks resolution separately
let CURRENT_FILE_HASH = null;
let SESSION_CONFLICT_REGISTRY = new Map(); // Maps conflict key -> { conflictId, number, isResolved }
let NEXT_CONFLICT_NUMBER = 1; // Always increments, never resets during session

// Reset conflict numbering for new file uploads
const checkAndResetForNewFile = (fileIdentifier) => {
  if (CURRENT_FILE_HASH !== fileIdentifier) {
    CURRENT_FILE_HASH = fileIdentifier;
    console.log(`New file detected: ${fileIdentifier}, resetting conflict session`);
    // Reset session registry for new file
    SESSION_CONFLICT_REGISTRY.clear();
    NEXT_CONFLICT_NUMBER = 1;
  }
};

// Initialize room settings
const initializeRoomSettings = () => {
  if (!currentRoomSettings) {
    currentRoomSettings = loadRoomSettings();
    const { availableRooms, roomCapacity } = convertToAllocationFormat(currentRoomSettings);
    AVAILABLE_ROOMS = availableRooms;
    ROOM_CAPACITY = roomCapacity;
  }
};

// Update room settings dynamically
export const updateRoomConfiguration = (newRoomSettings) => {
  currentRoomSettings = newRoomSettings;
  const { availableRooms, roomCapacity } = convertToAllocationFormat(newRoomSettings);
  AVAILABLE_ROOMS = availableRooms;
  ROOM_CAPACITY = roomCapacity;
};

// Get current room settings
export const getCurrentRoomSettings = () => {
  initializeRoomSettings();
  return currentRoomSettings;
};

// Reset session conflict number registry (for new file uploads)
export const resetConflictNumbersForNewFile = () => {
  CURRENT_FILE_HASH = null;
  SESSION_CONFLICT_REGISTRY.clear();
  NEXT_CONFLICT_NUMBER = 1;
  console.log("Conflict numbering reset for new file upload - session registry cleared");
};

// Debug function to check current conflict state
export const debugConflictNumberRegistry = () => {
  console.log("=== SESSION CONFLICT REGISTRY DEBUGGING ===");
  console.log(`Current file hash: ${CURRENT_FILE_HASH || "none"}`);
  console.log(`Next conflict number: ${NEXT_CONFLICT_NUMBER}`);
  console.log("Session conflict registry:");

  SESSION_CONFLICT_REGISTRY.forEach((value, key) => {
    console.log(
      `  ${key} -> ${value.conflictId} (number: ${value.number}, resolved: ${value.isResolved})`
    );
  });

  return {
    fileHash: CURRENT_FILE_HASH,
    nextNumber: NEXT_CONFLICT_NUMBER,
    registry: Object.fromEntries(SESSION_CONFLICT_REGISTRY),
  };
};

// Valid time slots (from macro)
const VALID_TIME_SLOTS = [
  "mon am",
  "mon pm",
  "mon eve",
  "tue am",
  "tue pm",
  "tue eve",
  "wed am",
  "wed pm",
  "wed eve",
  "thu am",
  "thu pm",
  "thu eve",
  "fri am",
  "fri pm", // No evening on Friday
  "sat am",
  "sat pm", // No evening on Saturday
];

// Format day and time to lowercase string, standardizing format by removing parentheticals
// Helper function to check if original time slots contain parentheses
const hasParentheses = (dayOfWeek, timeOfDay) => {
  const day = dayOfWeek ? dayOfWeek.toString().trim() : "";
  const time = timeOfDay ? timeOfDay.toString().trim() : "";
  return day.includes("(") || day.includes(")") || time.includes("(") || time.includes(")");
};

const formatDayTime = (dayOfWeek, timeOfDay) => {
  if (!dayOfWeek && !timeOfDay) return "";

  // Extract just day and time period
  let day = dayOfWeek ? dayOfWeek.toString().trim() : "";
  const time = timeOfDay ? timeOfDay.toString().trim() : "";

  // First step: Combine inputs and remove parenthetical content
  let combined = (day + " " + time)
    .trim()
    .replace(/\s*\([^)]*\)/g, "") // Remove anything in parentheses including parentheses
    .toLowerCase();

  // Clean up any double spaces created after removing parentheses
  combined = combined.replace(/\s+/g, " ").trim();

  // Verify the result matches expected day/time format patterns (mon/tue/wed/etc + am/pm/eve only)
  // This is the strict list of valid values
  const validDayPattern = /\b(mon|tue|wed|thu|fri|sat|sun)\b/;
  const validTimePattern = /\b(am|pm|eve)\b/;

  // Extract just the day and time period if they exist
  const dayMatch = combined.match(validDayPattern);
  const timeMatch = combined.match(validTimePattern);

  // Both day and time period MUST be present
  if (!dayMatch || !timeMatch) {
    console.log(`Skipping non-standard time slot: "${combined}" (original: "${day} ${time}")`);
    console.log(`  Reason: Missing ${!dayMatch ? "day" : "time period"} component`);
    return ""; // Return empty string to fail the dayTime check
  }

  // Construct the standardized format with just the matched day and time
  const standardized = `${dayMatch[0]} ${timeMatch[0]}`;

  // Debugging to show what was normalized
  if (standardized !== combined) {
    console.log(`Normalized time slot: "${combined}" → "${standardized}"`);
  }

  return standardized;
};

// Get combined day/time following VBA AutoAllocate logic (combines F and G)
const getCombinedDayTime = (row) => {
  // Follow the VBA FormatDayTime logic: combine F (day_time) and G (ew_time)
  return formatDayTime(row.day_time, row.ew_time);
};

// Get room capacity from dynamic settings
const getRoomCapacity = (roomNumber) => {
  initializeRoomSettings();
  return ROOM_CAPACITY[roomNumber] || 0;
};

// Sort rooms by capacity (ascending)
const sortRoomsByCapacity = (rooms) => {
  return rooms.sort((a, b) => a.capacity - b.capacity);
};

const SKIP_NO_ROOM = "No free room big enough at that day and time";

/**
 * Why a data row cannot be allocated, or "" when it can be.
 * Mirrors, in order, the checks the allocation loops apply.
 */
const getRowSkipReason = (row) => {
  if (!row.unit_name) {
    return "Unit Name (D) is empty";
  }

  const studentsField = row.students_coming;
  if (studentsField === undefined || studentsField === null || studentsField === "") {
    return "Students Coming (K) is empty";
  }
  if ((parseInt(studentsField) || 0) === 0) {
    return "Students Coming (K) is 0 or not a number";
  }

  if (!getCombinedDayTime(row)) {
    return "Day/Time (F) and EW/Time (G) are not given a day and a time period";
  }

  return "";
};

/**
 * Count what an allocation pass achieved, for the status message.
 * Returns { allocated, skipped, reasons: [{ reason, rows: [excelRow, ...] }, ...] }
 * with the most common reason first.
 */
export const summarizeAllocation = (data) => {
  const reasons = new Map();
  let allocated = 0;
  let skipped = 0;

  data.forEach((row) => {
    if (row.is_section_header) return;

    if (row.room_allocation) {
      allocated++;
      return;
    }

    if (row.skip_reason) {
      skipped++;
      if (!reasons.has(row.skip_reason)) {
        reasons.set(row.skip_reason, []);
      }
      if (row.row_index) {
        reasons.get(row.skip_reason).push(row.row_index);
      }
    }
  });

  return {
    allocated,
    skipped,
    reasons: [...reasons.entries()]
      .map(([reason, rows]) => ({ reason, rows: rows.sort((a, b) => a - b) }))
      .sort((a, b) => b.rows.length - a.rows.length),
  };
};

// Auto allocate rooms (equivalent to autoAllocateRooms_Click)
export const autoAllocateRooms = (data) => {
  initializeRoomSettings(); // Ensure room settings are loaded
  const updatedData = [...data];
  const dayTimeRooms = new Map(); // Track allocated rooms per time slot

  // Process rows
  for (let i = 0; i < updatedData.length; i++) {
    const row = updatedData[i];

    // Skip section headers
    if (row.is_section_header) continue;

    const skipReason = getRowSkipReason(row);
    if (skipReason) {
      updatedData[i] = { ...row, skip_reason: skipReason };
      continue;
    }

    const studentsCount = parseInt(row.students_coming) || 0;
    const dayTime = getCombinedDayTime(row);

    // Find available room
    const suggestedRoom = getAvailableRoomForAllocation(dayTimeRooms, dayTime, studentsCount);

    if (!suggestedRoom) {
      updatedData[i] = { ...row, skip_reason: SKIP_NO_ROOM };
      continue;
    }

    updatedData[i] = {
      ...row,
      room_auto_allo: suggestedRoom,
      room_allocation: suggestedRoom, // CRITICAL: Update both fields for consistency
      skip_reason: "",
    };

    // Track allocation
    const key = `${dayTime}|${suggestedRoom}`;
    dayTimeRooms.set(key, true);
  }

  return updatedData;
};

// Auto allocate only pending rooms (rooms that haven't been allocated yet)
export const autoAllocatePendingRooms = (data) => {
  initializeRoomSettings(); // Ensure room settings are loaded
  const updatedData = [...data];
  const dayTimeRooms = new Map(); // Track allocated rooms per time slot

  // First, track all existing allocations to avoid conflicts
  for (let i = 0; i < updatedData.length; i++) {
    const row = updatedData[i];

    // Skip if no allocation exists
    if (!row.room_allocation || !row.room_auto_allo) continue;

    const dayTime = getCombinedDayTime(row);
    if (!dayTime) continue;

    // Track existing allocation
    const key = `${dayTime}|${row.room_allocation}`;
    dayTimeRooms.set(key, true);
  }

  // Process rows that don't have allocations yet
  for (let i = 0; i < updatedData.length; i++) {
    const row = updatedData[i];

    // Skip if already allocated
    if (row.room_allocation && row.room_auto_allo) continue;

    // Skip section headers
    if (row.is_section_header) continue;

    const skipReason = getRowSkipReason(row);
    if (skipReason) {
      updatedData[i] = { ...row, skip_reason: skipReason };
      continue;
    }

    const studentsCount = parseInt(row.students_coming) || 0;
    const dayTime = getCombinedDayTime(row);

    // Find available room
    const suggestedRoom = getAvailableRoomForAllocation(dayTimeRooms, dayTime, studentsCount);

    if (!suggestedRoom) {
      updatedData[i] = { ...row, skip_reason: SKIP_NO_ROOM };
      continue;
    }

    updatedData[i] = {
      ...row,
      room_auto_allo: suggestedRoom,
      room_allocation: suggestedRoom, // CRITICAL: Update both fields for consistency
      skip_reason: "",
    };

    // Track allocation
    const key = `${dayTime}|${suggestedRoom}`;
    dayTimeRooms.set(key, true);
  }

  return updatedData;
};

// Get available room for allocation (equivalent to GetAvailableRoomForAllocationNew)
const getAvailableRoomForAllocation = (dayTimeRooms, dayTime, studentsCount) => {
  initializeRoomSettings(); // Ensure room settings are loaded
  // Rooms come from Room Settings, never from the uploaded file
  const suitableRooms = AVAILABLE_ROOMS.filter(
    (room) => getRoomCapacity(room) >= studentsCount
  ).map((room) => ({ room: room, capacity: getRoomCapacity(room) }));

  if (suitableRooms.length === 0) return "";

  // Sort by capacity (smallest first)
  const sortedRooms = sortRoomsByCapacity(suitableRooms);

  // Find first available room
  for (const roomInfo of sortedRooms) {
    const key = `${dayTime}|${roomInfo.room}`;
    if (!dayTimeRooms.has(key)) {
      return roomInfo.room;
    }
  }

  return "";
};

// Check for duplicate allocations (equivalent to checkDuplicateAllocations)
export const checkDuplicateAllocations = (data, existingConflicts = [], fileIdentifier = null) => {
  initializeRoomSettings(); // Ensure room settings are loaded

  // Check if this is a new file and reset conflict numbers if needed
  if (fileIdentifier) {
    checkAndResetForNewFile(fileIdentifier);
  }

  const allocatedRooms = new Map(); // Maps key -> array of row indices
  const conflictMap = new Map(); // Maps key -> conflict object for easy lookup
  const conflicts = [];

  // Update session registry based on existing conflicts (mark as resolved or active)
  const currentConflictKeys = new Set();

  // First pass: identify all current conflict keys from data
  data.forEach((row, index) => {
    if (row.is_section_header) return;

    const dayTime = getCombinedDayTime(row);
    const room = row.room_auto_allo;

    if (!dayTime || !room) return;

    const key = `${dayTime}-${room}`;
    if (allocatedRooms.has(key)) {
      currentConflictKeys.add(key);
    } else {
      allocatedRooms.set(key, [index]);
    }
  });

  // Mark conflicts as resolved if they're not in current data
  SESSION_CONFLICT_REGISTRY.forEach((value, key) => {
    if (!currentConflictKeys.has(key)) {
      value.isResolved = true;
      console.log(`Marked conflict ${value.conflictId} as RESOLVED (key: ${key})`);
    } else {
      value.isResolved = false;
      console.log(`Conflict ${value.conflictId} is still ACTIVE (key: ${key})`);
    }
  });

  // Helper function to get or create conflict number for a key
  const getConflictIdForKey = (key) => {
    if (SESSION_CONFLICT_REGISTRY.has(key)) {
      // Use existing conflict ID from session registry
      const entry = SESSION_CONFLICT_REGISTRY.get(key);
      entry.isResolved = false; // Mark as active since we found it again
      console.log(`PRESERVED from session: ${entry.conflictId} for key: ${key}`);
      return entry.conflictId;
    } else {
      // Create new conflict ID and add to session registry
      const conflictNumber = NEXT_CONFLICT_NUMBER++;
      const conflictId = `Conflict #${conflictNumber}`;

      SESSION_CONFLICT_REGISTRY.set(key, {
        conflictId,
        number: conflictNumber,
        isResolved: false,
      });

      console.log(`NEW conflict: ${conflictId} for key: ${key}`);
      return conflictId;
    }
  };

  // Reset allocatedRooms for actual conflict detection
  allocatedRooms.clear();

  data.forEach((row, index) => {
    // Skip section headers
    if (row.is_section_header) return;

    // Use VBA conflict detection logic (combines F and G like auto allocation)
    const dayTime = getCombinedDayTime(row);
    const room = row.room_auto_allo;

    if (!dayTime || !room) return;

    const key = `${dayTime}-${room}`;

    if (allocatedRooms.has(key)) {
      // Found conflict - check if we already have a conflict object for this key
      if (conflictMap.has(key)) {
        // Extend existing conflict with this new row
        const existingConflict = conflictMap.get(key);

        // Add new row to conflicting_data
        existingConflict.conflicting_data.push({
          row_number: row.row_index,
          item_no: row.item_no || "",
          unit_name: row.unit_name || "",
          tutor_name: row.tutor_name || "",
          students_coming: row.students_coming || "",
          group: row.group || "",
          intake: row.intake || "",
        });

        // Update conflict_rows string with all row numbers
        const allRowIndices = allocatedRooms.get(key).concat([index]);
        const allRowNumbers = allRowIndices.map((idx) => data[idx].row_index);
        existingConflict.conflict_rows = allRowNumbers.join(",");

        // Update parentheses detection for the existing conflict
        const currentHasParentheses = allRowIndices.some((idx) => {
          const conflictRow = data[idx];
          return hasParentheses(conflictRow.day_time, conflictRow.ew_time);
        });
        existingConflict.has_parentheses = currentHasParentheses;

        // Update the allocated rooms array
        allocatedRooms.get(key).push(index);
      } else {
        // First time we detect this conflict - create conflict object
        const existingIndices = Array.isArray(allocatedRooms.get(key))
          ? allocatedRooms.get(key)
          : [allocatedRooms.get(key)];

        const currentRowIndex = index;

        // Format day/time for display (proper case)
        const parts = dayTime.split(" ");
        const formattedDayTime =
          parts[1]?.toLowerCase() === "eve"
            ? `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)} Eve`
            : `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)} ${parts[1]?.toUpperCase()}`;

        // Get conflict ID using session registry
        const conflictId = getConflictIdForKey(key);

        const conflictingData = [];

        // Add all existing rows
        existingIndices.forEach((idx) => {
          const existingRow = data[idx];
          conflictingData.push({
            row_number: existingRow.row_index,
            item_no: existingRow.item_no || "",
            unit_name: existingRow.unit_name || "",
            tutor_name: existingRow.tutor_name || "",
            students_coming: existingRow.students_coming || "",
            group: existingRow.group || "",
            intake: existingRow.intake || "",
          });
        });

        // Add current row
        conflictingData.push({
          row_number: row.row_index,
          item_no: row.item_no || "",
          unit_name: row.unit_name || "",
          tutor_name: row.tutor_name || "",
          students_coming: row.students_coming || "",
          group: row.group || "",
          intake: row.intake || "",
        });

        // Get all row numbers for conflict_rows string
        const allIndices = existingIndices.concat([currentRowIndex]);
        const allRowNumbers = allIndices.map((idx) => data[idx].row_index);

        // Check if any of the conflicting rows have parentheses in their original time slots
        const hasParenthesesInConflict = allIndices.some((idx) => {
          const conflictRow = data[idx];
          return hasParentheses(conflictRow.day_time, conflictRow.ew_time);
        });

        // Create conflict entry with the session-based conflict ID
        const conflict = {
          conflict_id: conflictId,
          day_time: formattedDayTime,
          conflict_room: room,
          conflict_rows: allRowNumbers.join(","),
          conflicting_data: conflictingData,
          has_parentheses: hasParenthesesInConflict, // New field for UI styling
        };

        conflicts.push(conflict);
        conflictMap.set(key, conflict);

        // Update allocated rooms to be an array
        allocatedRooms.set(key, allIndices);
      }
    } else {
      // First occurrence of this time/room combination
      allocatedRooms.set(key, [index]); // Store as array for consistency
    }
  });

  console.log(`Final conflicts: ${conflicts.length} found`);
  conflicts.forEach((c) => console.log(`  ${c.conflict_id}: ${c.day_time} in ${c.conflict_room}`));

  return conflicts;
};

// Get session conflict statistics
export const getSessionConflictStats = () => {
  const stats = {
    total: SESSION_CONFLICT_REGISTRY.size,
    active: 0,
    resolved: 0,
    activeConflicts: [],
    resolvedConflicts: [],
  };

  SESSION_CONFLICT_REGISTRY.forEach((value, key) => {
    if (value.isResolved) {
      stats.resolved++;
      stats.resolvedConflicts.push({
        id: value.conflictId,
        number: value.number,
        key: key,
      });
    } else {
      stats.active++;
      stats.activeConflicts.push({
        id: value.conflictId,
        number: value.number,
        key: key,
      });
    }
  });

  return stats;
};

// Find unallocated rooms (equivalent to FindUnallocatedRooms)
export const findUnallocatedRooms = (data) => {
  initializeRoomSettings(); // Ensure room settings are loaded
  const unallocatedResults = [];

  // Check each valid time slot
  VALID_TIME_SLOTS.forEach((timeSlot) => {
    const unallocatedRooms = getAvailableRoomsForTimeSlot(data, timeSlot);

    if (unallocatedRooms.length > 0) {
      // Format day/time for display
      const parts = timeSlot.split(" ");
      const formattedDayTime =
        parts[1].toLowerCase() === "eve"
          ? `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)} Eve`
          : `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)} ${parts[1].toUpperCase()}`;

      unallocatedResults.push({
        unallocated_time: formattedDayTime,
        unallocated_rooms: unallocatedRooms.join("-"),
      });
    }
  });

  return unallocatedResults;
};

// Helper function to get available rooms for a specific time slot
// This ensures consistency between "Find Unallocated" and conflict resolution
export const getAvailableRoomsForTimeSlot = (data, timeSlot) => {
  initializeRoomSettings(); // Ensure room settings are loaded
  const allocatedDict = new Map();

  // Collect all allocated rooms by time slot - use SAME format as auto allocation
  data.forEach((row, index) => {
    // Skip section headers
    if (row.is_section_header) return;

    // Get day/time using SAME logic as auto allocation (combines F and G)
    const dayTime = getCombinedDayTime(row);
    const room = row.room_auto_allo;

    if (!dayTime || !room) return;

    if (allocatedDict.has(dayTime)) {
      const existing = allocatedDict.get(dayTime);
      allocatedDict.set(dayTime, `${existing}-${room}`);
    } else {
      allocatedDict.set(dayTime, room);
    }
  });

  const unallocatedRooms = [];

  if (allocatedDict.has(timeSlot)) {
    // Get list of allocated rooms for this time
    const allocatedRooms = allocatedDict.get(timeSlot).split("-");

    // Check each possible room
    AVAILABLE_ROOMS.forEach((room) => {
      if (!allocatedRooms.includes(room)) {
        unallocatedRooms.push(room);
      }
    });
  } else {
    // No rooms allocated, all rooms are available
    unallocatedRooms.push(...AVAILABLE_ROOMS);
  }

  return unallocatedRooms;
};

// Clear auto allocation (equivalent to clearAutoAllocation__Click)
export const clearAutoAllocation = (data) => {
  return data.map((row) => ({
    ...row,
    room_auto_allo: "", // Clear room auto allocation column
    room_allocation: "", // CRITICAL: Clear both fields for consistency
    skip_reason: "", // Drop reasons from the previous pass
  }));
};

// Clear all analysis columns (equivalent to clearALLCol_Click)
export const clearAllColumns = (data) => {
  return data.map((row) => ({
    ...row,
    // Clear analysis columns - will need to add these to data structure
    conflict_rows: "",
    conflict_room: "",
    unallocated_time: "",
    unallocated_rooms: "",
  }));
};
